import { YMaps, Map, Placemark } from '@pbe/react-yandex-maps';

export interface MapCoords {
  lat: number;
  lng: number;
}

interface YMapEvent {
  get: (key: string) => unknown;
}

interface MapViewProps {
  lat?: number | null;
  lng?: number | null;
  zoom?: number;
  className?: string;
  /** When true, the user can click the map / drag the marker to pick a point. */
  selectable?: boolean;
  /** Fallback center used when no point is selected yet. Defaults to Tashkent. */
  defaultCenter?: [number, number];
  onChange?: (coords: MapCoords) => void;
}

const DEFAULT_CENTER: [number, number] = [41.311081, 69.240562];

/**
 * Yandex map (via @pbe/react-yandex-maps, no API key required).
 * - Read-only when `selectable` is false: behaviors are disabled and a
 *   transparent overlay swallows any pointer events.
 * - Interactive when `selectable` is true: click the map or drag the marker to
 *   choose a location; the chosen [lat, lng] is reported through `onChange`.
 *
 * `controls` is intentionally kept empty — passing named controls (e.g.
 * `geolocationControl`) can throw "s is not a constructor" from the Yandex API.
 * The parent must have a defined height (width/height are 100%).
 */
export function MapView({
  lat,
  lng,
  zoom = 12,
  className,
  selectable = false,
  defaultCenter = DEFAULT_CENTER,
  onChange,
}: MapViewProps) {
  const hasPoint = typeof lat === 'number' && typeof lng === 'number';
  const center: [number, number] = hasPoint ? [lat as number, lng as number] : defaultCenter;

  const defaultState = selectable
    ? { center, zoom, controls: [] }
    : { center, zoom, controls: [], behaviors: [] };

  const handleMapClick = (e: YMapEvent) => {
    if (!selectable) return;
    const coords = e.get('coords') as [number, number] | undefined;
    if (coords) onChange?.({ lat: coords[0], lng: coords[1] });
  };

  const handleDragEnd = (e: YMapEvent) => {
    const target = e.get('target') as { geometry?: { getCoordinates?: () => [number, number] } } | undefined;
    const coords = target?.geometry?.getCoordinates?.();
    if (coords) onChange?.({ lat: coords[0], lng: coords[1] });
  };

  return (
    <div className={`relative ${className ?? ''}`}>
      <YMaps>
        <Map
          defaultState={defaultState}
          width='100%'
          height='100%'
          onClick={handleMapClick}
          options={{ suppressMapOpenBlock: true, yandexMapDisablePoiInteractivity: true }}
        >
          {hasPoint && (
            <Placemark
              geometry={[lat as number, lng as number]}
              options={{ draggable: selectable, preset: 'islands#violetDotIcon' }}
              onDragEnd={handleDragEnd}
            />
          )}
        </Map>
      </YMaps>
      {!selectable && <div className='absolute inset-0 z-[1]' aria-hidden />}
    </div>
  );
}
